import React, { useMemo, useState } from 'react';

import EntityDropDownFilterContainerComponent from 'components/entityViewer/entitiesDropDownFilter/EntityDropDownFilterContainer';
import {
    useExternalSkuSearch,
    useFacilityOptions,
    useStatusOptions,
} from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import { useDebouncedValue } from 'shared';

import { FILTER_QUERY_KEYS, PartnerPurchaseOrderFilterConfigArray } from './PartnerPurchaseOrderFilterConfig';

/**
 * Slim filter bar for partner purchase orders.
 *
 * Facility options are whitelisted to the selected FBM account's facilityIDs when provided.
 *
 * @param {Object} props
 * @param {Object} [props.initialValues={}]
 * @param {(nextFilters: Object) => void} [props.onChange]
 * @param {number[]} [props.whitelistedFacilityIds]
 */
export function PartnerPurchaseOrderFilters({ initialValues = {}, onChange, whitelistedFacilityIds }) {
    const [externalSkuSearchTerm, setExternalSkuSearchTerm] = useState('');
    const debouncedSkuSearch = useDebouncedValue(externalSkuSearchTerm, 300);
    const shouldFetchExternalSkus = typeof debouncedSkuSearch === 'string' && debouncedSkuSearch.trim().length >= 2;

    const { data: statusOptions = [], isLoading: isLoadingStatuses } = useStatusOptions();
    const { data: facilityOptions = [], isLoading: isLoadingFacilities } = useFacilityOptions();
    const {
        data: externalSkuOptions = [],
        isError: hasExternalSkuError,
        isLoading: isLoadingExternalSkus,
    } = useExternalSkuSearch(debouncedSkuSearch, {
        enabled: shouldFetchExternalSkus,
    });

    const whitelistedFacilityIdSet = useMemo(() => {
        if (!Array.isArray(whitelistedFacilityIds) || whitelistedFacilityIds.length === 0) {
            return null;
        }
        return new Set(whitelistedFacilityIds.map((id) => Number(id)));
    }, [whitelistedFacilityIds]);

    const filteredFacilityOptions = useMemo(() => {
        if (!whitelistedFacilityIdSet) return facilityOptions;
        return facilityOptions.filter((option) => whitelistedFacilityIdSet.has(Number(option.value)));
    }, [facilityOptions, whitelistedFacilityIdSet]);

    const effectiveFilterConfigs = useMemo(
        () =>
            PartnerPurchaseOrderFilterConfigArray.map((cfg) => {
                const queryId = cfg.remoteOptions?.queryId;

                if (queryId === FILTER_QUERY_KEYS.STATUSES) {
                    return {
                        ...cfg,
                        options: statusOptions,
                        menuProps: {
                            ...cfg.menuProps,
                            isLoading: isLoadingStatuses,
                        },
                    };
                }

                if (queryId === FILTER_QUERY_KEYS.FACILITIES) {
                    return {
                        ...cfg,
                        options: filteredFacilityOptions,
                        menuProps: {
                            ...cfg.menuProps,
                            isLoading: isLoadingFacilities,
                        },
                    };
                }

                if (queryId === FILTER_QUERY_KEYS.EXTERNAL_SKU) {
                    const minQueryLength = cfg.menuProps?.minQueryLength ?? 2;
                    const showRemotePrompt = (externalSkuSearchTerm || '').length < minQueryLength;

                    return {
                        ...cfg,
                        options: externalSkuOptions,
                        menuProps: {
                            ...cfg.menuProps,
                            query: externalSkuSearchTerm,
                            onQueryChange: setExternalSkuSearchTerm,
                            isLoading: isLoadingExternalSkus,
                            hasError: hasExternalSkuError,
                            showRemotePrompt,
                        },
                    };
                }

                return cfg;
            }),
        [
            externalSkuOptions,
            externalSkuSearchTerm,
            filteredFacilityOptions,
            hasExternalSkuError,
            isLoadingExternalSkus,
            isLoadingFacilities,
            isLoadingStatuses,
            statusOptions,
        ],
    );

    const handleFilterChange = (filterValues) => {
        if (onChange) {
            onChange(filterValues);
        }
    };

    return (
        <EntityDropDownFilterContainerComponent
            title="Purchase Order Filters"
            filterConfigs={effectiveFilterConfigs}
            initialValues={initialValues}
            onChange={handleFilterChange}
            autoApply
        />
    );
}
